import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../firebase';
import { doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import featureGatingService from '../services/featureGatingService';

function InternshipForm() {
  const { globalUser } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [userCategory, setUserCategory] = useState('');
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    preferredName: '',
    regNumber: '',
    department: '',
    fieldOfStudy: '',
    cgpa: '',
    promoCode: '',
    permanentAddress: '',
    experience: '',
  });

  // File state
  const [resumeFile, setResumeFile] = useState(null);
  const [coverLetterFile, setCoverLetterFile] = useState(null);

  // Per-field validation errors, keyed by field name.
  const [errors, setErrors] = useState({});

  // Field-name → human label, used inside validation messages.
  const FIELD_LABELS = {
    firstName: 'First name', lastName: 'Last name', preferredName: 'Preferred name',
    regNumber: 'Registration number', department: 'Department', fieldOfStudy: 'Field of study',
    cgpa: 'CGPA', permanentAddress: 'Permanent address', experience: 'Experience',
  };

  const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5MB
  const RESUME_EXT = /\.(pdf|docx?)$/i;

  // Shared input className — adds a red ring when the field has an error.
  const inputClass = (name) =>
    `w-full px-4 py-3 rounded-lg bg-gray-200 text-black placeholder-gray-500 focus:outline-none ${
      errors[name] ? 'ring-2 ring-red-500' : 'focus:ring-2 focus:ring-[#C99F4A]'
    }`;

  // Renders an inline error message for a field (or nothing).
  const fieldError = (name) =>
    errors[name] ? (
      <p className="text-red-400 text-xs mt-1 ml-1">{errors[name]}</p>
    ) : null;

  useEffect(() => {
    checkUserCategoryAndSubscription();
    
  }, [globalUser]);

  const checkUserCategoryAndSubscription = async () => {
    if (!globalUser) return;

    try {
      setIsCheckingSubscription(true);

      // Fetch user category
      const userDocRef = doc(db, 'lawyers', globalUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const data = userDoc.data();
        const category = data.userCategory || data.userCateogry || '';
        setUserCategory(category);

        // Only check subscription if user is a law student
        if (category === 'Law student') {
          // Check if user has law student subscription
          const subscriptionDocRef = doc(db, 'subscriptions', globalUser.uid);
          const subscriptionDoc = await getDoc(subscriptionDocRef);

          if (subscriptionDoc.exists()) {
            const subscriptionData = subscriptionDoc.data();
            const transactions = subscriptionData.transactions || [];

            // Check for active law student subscription
            const now = new Date();
            const hasActiveSubscription = transactions.some(transaction => {
              let endDate;
              if (transaction['end-time']?.toDate) {
                endDate = transaction['end-time'].toDate();
              } else {
                endDate = new Date(transaction['end-time']);
              }

              const isActive = endDate > now;
              const isLawStudent = transaction.type === 'lawStudent' ||
                                   transaction.category === 'lawStudent';

              return isActive && isLawStudent;
            });

            setHasSubscription(hasActiveSubscription);
            //setHasSubscription(true);
          }
        }
      }
    } catch (error) {
      console.error('Error checking user category and subscription:', error);
    } finally {
      setIsCheckingSubscription(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear this field's error as soon as the user edits it.
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    const errKey = type === 'resume' ? 'resume' : 'coverLetter';
    const label = type === 'resume' ? 'Resume' : 'Cover letter';

    // Validate the chosen file up-front so the user sees the problem immediately.
    if (!RESUME_EXT.test(file.name)) {
      setErrors((prev) => ({ ...prev, [errKey]: `${label} must be a PDF or Word document.` }));
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setErrors((prev) => ({ ...prev, [errKey]: `${label} must be 5MB or smaller.` }));
      return;
    }
    if (errors[errKey]) setErrors((prev) => ({ ...prev, [errKey]: undefined }));

    if (type === 'resume') setResumeFile(file);
    else setCoverLetterFile(file);
  };

  // Returns an `errors` object — empty when the form is valid.
  const validateForm = () => {
    const errs = {};
    const trimmed = Object.fromEntries(
      Object.entries(formData).map(([k, v]) => [k, (v || '').trim()]),
    );

    // Names — letters, spaces, dots, hyphens and apostrophes; 2–50 chars.
    const NAME_RE = /^[A-Za-z][A-Za-z\s.'-]{1,49}$/;
    ['firstName', 'lastName', 'preferredName'].forEach((f) => {
      if (!trimmed[f]) errs[f] = `${FIELD_LABELS[f]} is required.`;
      else if (!NAME_RE.test(trimmed[f])) errs[f] = `${FIELD_LABELS[f]} must be 2–50 letters.`;
    });

    if (!trimmed.regNumber) errs.regNumber = 'Registration number is required.';
    else if (!/^[A-Za-z0-9./-]{3,30}$/.test(trimmed.regNumber))
      errs.regNumber = 'Registration number must be 3–30 letters/digits.';

    if (!trimmed.department) errs.department = 'Department is required.';
    else if (trimmed.department.length < 2 || trimmed.department.length > 100)
      errs.department = 'Department must be 2–100 characters.';

    if (!trimmed.fieldOfStudy) errs.fieldOfStudy = 'Field of study is required.';
    else if (trimmed.fieldOfStudy.length < 2 || trimmed.fieldOfStudy.length > 100)
      errs.fieldOfStudy = 'Field of study must be 2–100 characters.';

    if (!trimmed.cgpa) errs.cgpa = 'CGPA is required.';
    else {
      const n = Number(trimmed.cgpa);
      if (Number.isNaN(n) || n < 0 || n > 10) errs.cgpa = 'CGPA must be a number between 0 and 10.';
    }

    if (trimmed.promoCode && trimmed.promoCode.length > 30)
      errs.promoCode = 'Subscription code must be at most 30 characters.';

    if (!trimmed.permanentAddress) errs.permanentAddress = 'Permanent address is required.';
    else if (trimmed.permanentAddress.length < 10 || trimmed.permanentAddress.length > 300)
      errs.permanentAddress = 'Permanent address must be 10–300 characters.';

    if (!trimmed.experience) errs.experience = 'Experience is required.';
    else if (trimmed.experience.length > 500)
      errs.experience = 'Experience must be at most 500 characters.';

    // Files
    if (!resumeFile) errs.resume = 'Please upload your resume.';
    else if (!RESUME_EXT.test(resumeFile.name)) errs.resume = 'Resume must be a PDF or Word document.';
    else if (resumeFile.size > MAX_FILE_BYTES) errs.resume = 'Resume must be 5MB or smaller.';

    if (coverLetterFile) {
      if (!RESUME_EXT.test(coverLetterFile.name))
        errs.coverLetter = 'Cover letter must be a PDF or Word document.';
      else if (coverLetterFile.size > MAX_FILE_BYTES)
        errs.coverLetter = 'Cover letter must be 5MB or smaller.';
    }

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errs = validateForm();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      // Surface the first error in a toast so the user notices.
      const firstMsg = Object.values(errs).find(Boolean);
      if (firstMsg) toast.error(firstMsg);
      return;
    }
    setErrors({});

    setIsLoading(true);

    try {
      // Upload the resume (and cover letter, if provided) to Firebase Storage
      // so the admin portal can view and share them with lawyers. Uses the
      // existing `resumes/` path so no new Storage rules are required.
      const resumePath = `resumes/${globalUser.uid}/${Date.now()}_${resumeFile.name}`;
      const resumeRef = storageRef(storage, resumePath);
      await uploadBytes(resumeRef, resumeFile);
      const resumeUrl = await getDownloadURL(resumeRef);

      let coverLetterUrl = null;
      if (coverLetterFile) {
        const coverPath = `resumes/${globalUser.uid}/${Date.now()}_${coverLetterFile.name}`;
        const coverRef = storageRef(storage, coverPath);
        await uploadBytes(coverRef, coverLetterFile);
        coverLetterUrl = await getDownloadURL(coverRef);
      }

      // Save to Firestore
      const internshipApplicationData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        preferredName: formData.preferredName,
        email: globalUser.email,
        userId: globalUser.uid,
        regNumber: formData.regNumber,
        department: formData.department,
        fieldOfStudy: formData.fieldOfStudy,
        cgpa: formData.cgpa,
        promoCode: formData.promoCode || 'Not Provided',
        permanentAddress: formData.permanentAddress,
        experience: formData.experience,
        resumeUrl,
        resumeFileName: resumeFile.name,
        coverLetterUrl,
        coverLetterFileName: coverLetterFile ? coverLetterFile.name : null,
        sharedToLawyerIds: [],
        status: 'pending', // Can be: pending, under_review, accepted, rejected
        submittedAt: serverTimestamp(),
        createdAt: new Date().toISOString()
      };

      // Save to Firestore collection
      const docRef = await addDoc(collection(db, 'internshipApplications'), internshipApplicationData);
      console.log('Application saved to Firestore with ID:', docRef.id);

      // Send email notification
      const emailParams = {
        name: `${formData.firstName} ${formData.lastName}`,
        email: globalUser.email,
        phoneNumber: globalUser.phoneNumber || 'Not provided',
        query: `Internship Application

Preferred Name: ${formData.preferredName}
Registration Number: ${formData.regNumber}
Department: ${formData.department}
Field of Study: ${formData.fieldOfStudy}
CGPA: ${formData.cgpa}
Promo Code: ${formData.promoCode || 'Not Provided'}
Permanent Address: ${formData.permanentAddress}
Experience: ${formData.experience}
Resume: ${resumeFile.name}
Cover Letter: ${coverLetterFile ? coverLetterFile.name : 'Not provided'}

Application ID: ${docRef.id}`,
      };

      // Send application email to admin
      await axios.post(
        'https://jurident-feedback-backend-596718606544.us-central1.run.app/sendQuery',
        emailParams
      );

      // Send thank you email to user
      await axios.post(
        'https://jurident-feedback-backend-596718606544.us-central1.run.app/send-enquiry-thank-you',
        {
          name: `${formData.firstName} ${formData.lastName}`,
          email: globalUser.email
        }
      );

      toast.success('Application submitted successfully! Please check your email.');

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        preferredName: '',
        regNumber: '',
        department: '',
        fieldOfStudy: '',
        cgpa: '',
        promoCode: '',
        permanentAddress: '',
        experience: '',
      });
      setResumeFile(null);
      setCoverLetterFile(null);
      setErrors({});

    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgradeClick = () => {
    navigate('/portal/subscription');
  };

  // Show loading while checking subscription
  if (isCheckingSubscription) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-[#050125] flex items-center justify-center">
          <div className="text-white text-xl">Loading...</div>
        </div>
        <Footer />
      </>
    );
  }

  // Redirect if not a law student
  if (userCategory !== 'Law student') {
    return (
      <>
        <Navbar />
        <div className="internship-page min-h-screen bg-[#050125] flex items-center justify-center px-4">
          <div className="bg-[#1E1E45] rounded-2xl p-8 border-2 border-white max-w-md text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Access Restricted</h2>
            <p className="text-white mb-6">
              Internship form is only available for Law Students.
            </p>
            <button
              onClick={() => navigate('/portal/Dashboard')}
              className="bg-[#C99F4A] hover:bg-[#D19B15] text-white px-6 py-3 rounded-lg font-semibold transition"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Show subscription required message if no subscription
  if (!hasSubscription) {
    return (
      <>
        <Navbar />
        <div className="internship-page min-h-screen bg-[#050125] flex items-center justify-center px-4">
          <div className="bg-[#1E1E45] rounded-2xl p-8 border-2 border-white max-w-md text-center">
            <div className="w-16 h-16 bg-[#C99F4A] rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Subscription Required</h2>
            <p className="text-white mb-6">
              Please subscribe to access the internship form and confirm your internship through our application.
            </p>
            <button
              onClick={handleUpgradeClick}
              className="bg-[#C99F4A] hover:bg-[#D19B15] text-white px-6 py-3 rounded-lg font-semibold transition"
            >
              Subscribe Now
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Show the form if user is a law student with subscription
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-[#050125] py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold text-[#C99F4A] text-center mb-8">
            Internship Form
          </h1>

          <form onSubmit={handleSubmit} className="bg-[#1E1E45] rounded-2xl p-8 border-2 border-white">
            {/* Name Section */}
            <div className="mb-6">
              <label className="block text-white font-bold mb-3">Name</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="First Name"
                    maxLength={50}
                    className={inputClass('firstName')}
                    required
                  />
                  {fieldError('firstName')}
                </div>
                <div>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Last Name"
                    maxLength={50}
                    className={inputClass('lastName')}
                    required
                  />
                  {fieldError('lastName')}
                </div>
              </div>
            </div>

            {/* Preferred Name */}
            <div className="mb-6">
              <input
                type="text"
                name="preferredName"
                value={formData.preferredName}
                onChange={handleInputChange}
                placeholder="Preferred Name"
                maxLength={50}
                className={inputClass('preferredName')}
                required
              />
              {fieldError('preferredName')}
            </div>

            {/* College Details */}
            <div className="mb-6">
              <label className="block text-white font-bold mb-3">College Details</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <input
                    type="text"
                    name="regNumber"
                    value={formData.regNumber}
                    onChange={handleInputChange}
                    placeholder="Reg. No."
                    maxLength={30}
                    className={inputClass('regNumber')}
                    required
                  />
                  {fieldError('regNumber')}
                </div>
                <div>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    placeholder="Department"
                    maxLength={100}
                    className={inputClass('department')}
                    required
                  />
                  {fieldError('department')}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <input
                    type="text"
                    name="fieldOfStudy"
                    value={formData.fieldOfStudy}
                    onChange={handleInputChange}
                    placeholder="Field of Study"
                    maxLength={100}
                    className={inputClass('fieldOfStudy')}
                    required
                  />
                  {fieldError('fieldOfStudy')}
                </div>
                <div>
                  <input
                    type="text"
                    name="cgpa"
                    value={formData.cgpa}
                    onChange={handleInputChange}
                    placeholder="CGPA (0–10)"
                    inputMode="decimal"
                    className={inputClass('cgpa')}
                    required
                  />
                  {fieldError('cgpa')}
                </div>
              </div>
            </div>

            {/* Promo Code */}
            <div className="mb-6">
              <input
                type="text"
                name="promoCode"
                value={formData.promoCode}
                onChange={handleInputChange}
                placeholder="Subscription Code (Optional)"
                maxLength={30}
                className={inputClass('promoCode')}
              />
              {fieldError('promoCode')}
              <p className="text-gray-400 text-sm mt-2 ml-2 italic">
                Given by your college for discounted offer
              </p>
            </div>

            {/* Permanent Address */}
            <div className="mb-6">
              <label className="block text-white font-bold mb-3">Permanent Address</label>
              <textarea
                name="permanentAddress"
                value={formData.permanentAddress}
                onChange={handleInputChange}
                placeholder="Permanent Address"
                rows="3"
                maxLength={300}
                className={`${inputClass('permanentAddress')} resize-none`}
                required
              />
              {fieldError('permanentAddress')}
            </div>

            {/* Experience */}
            <div className="mb-6">
              <label className="block text-white font-bold mb-3">Experience</label>
              <input
                type="text"
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                placeholder="Experience"
                maxLength={500}
                className={inputClass('experience')}
                required
              />
              {fieldError('experience')}
            </div>

            {/* File Uploads */}
            <div className="mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="resume"
                    className={`block w-full bg-black hover:bg-gray-900 text-white px-6 py-3 rounded-lg font-semibold text-center cursor-pointer transition ${
                      errors.resume ? 'ring-2 ring-red-500' : ''
                    }`}
                  >
                    {resumeFile ? (
                      <span className="flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Resume Uploaded
                      </span>
                    ) : (
                      'Upload Resume *'
                    )}
                  </label>
                  <input
                    id="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileChange(e, 'resume')}
                    className="hidden"
                  />
                  {fieldError('resume')}
                  <p className="text-gray-500 text-xs mt-1 ml-1">PDF or Word, up to 5MB.</p>
                </div>
                <div>
                  <label
                    htmlFor="coverLetter"
                    className={`block w-full bg-black hover:bg-gray-900 text-white px-6 py-3 rounded-lg font-semibold text-center cursor-pointer transition ${
                      errors.coverLetter ? 'ring-2 ring-red-500' : ''
                    }`}
                  >
                    {coverLetterFile ? (
                      <span className="flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Cover Letter Uploaded
                      </span>
                    ) : (
                      'Upload Cover Letter'
                    )}
                  </label>
                  <input
                    id="coverLetter"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileChange(e, 'coverLetter')}
                    className="hidden"
                  />
                  {fieldError('coverLetter')}
                  <p className="text-gray-500 text-xs mt-1 ml-1">PDF or Word, up to 5MB (optional).</p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={isLoading}
                className="bg-black hover:bg-gray-900 text-white px-12 py-3 rounded-lg font-semibold text-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Submitting...' : 'Apply'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default InternshipForm;
